import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import supportApiService from '../../services/supportApiService';

const SupportTicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [comment, setComment] = useState('');
  const [addingComment, setAddingComment] = useState(false);

  useEffect(() => {
    if (id) {
      loadTicketDetails();
    }
  }, [id]);

  const loadTicketDetails = async () => {
    try {
      setLoading(true);
      console.log('🏨 [SUPPORT-TICKET-DETAILS] Loading ticket details for ID:', id);
      
      const response = await supportApiService.getTicketDetails(id);
      
      if (response.success) {
        console.log('✅ Ticket details loaded:', response);
        setTicket(response.tickets);
        toast.success('Ticket details loaded successfully');
      } else {
        console.error('❌ Failed to load ticket details:', response.message);
        toast.error(response.message || 'Failed to load ticket details');
        navigate('/support/dashboard/support-tickets');
      }
    } catch (error) {
      console.error('❌ Error loading ticket details:', error);
      toast.error('Failed to load ticket details');
      navigate('/support/dashboard/support-tickets');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!comment.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    try {
      setAddingComment(true);
      console.log('🏨 [SUPPORT-ADD-COMMENT] Adding comment to ticket:', id);
      
      const response = await supportApiService.addCommentToTicket(id, comment.trim());
      
      if (response.success) {
        console.log('✅ Comment added successfully');
        toast.success('Comment added successfully!');
        setComment('');
        // Reload ticket details to get updated comments
        await loadTicketDetails();
      } else {
        console.error('❌ Failed to add comment:', response.message);
        toast.error(response.message || 'Failed to add comment');
      }
    } catch (error) {
      console.error('❌ Error adding comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setAddingComment(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      'ASSIGNED': 'bg-blue-100 text-blue-800',
      'RESOLVE': 'bg-green-100 text-green-800',
      'INPROGRESS': 'bg-yellow-100 text-yellow-800',
      'PENDING': 'bg-gray-100 text-gray-800',
      'CLOSED': 'bg-red-100 text-red-800'
    };

    const colorClass = statusColors[status] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass}`}>
        {status}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return dateString;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-ticket-alt text-gray-400 text-2xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Ticket Not Found</h3>
          <p className="text-gray-600 mb-4">The requested ticket could not be found.</p>
          <button
            onClick={() => navigate('/support/dashboard/support-tickets')}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md"
          >
            Back to Tickets
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Ticket Details</h1>
            <p className="text-gray-600">View and manage support ticket information</p>
          </div>
          <button
            onClick={() => navigate('/support/dashboard/support-tickets')}
            className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-md flex items-center"
          >
            <i className="fas fa-arrow-left mr-2"></i>
            Back to Tickets
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Ticket Details */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-medium text-gray-900">Ticket Information</h3>
                  {getStatusBadge(ticket.ticketStatus)}
                </div>
              </div>

              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Basic Information */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Agent Name
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {ticket.agentName || 'N/A'}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Last Name
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {ticket.agentLastName || 'N/A'}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Email
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {ticket.agentEmail || 'N/A'}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Ticket ID
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded font-mono">
                        {ticket.ticketId || 'N/A'}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Agent ID
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {ticket.agentId || 'N/A'}
                      </div>
                    </div>
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Booking ID
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {ticket.bookingId || 'N/A'}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Refund ID
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {ticket.refundId || 'N/A'}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment ID
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {ticket.paymentId || 'N/A'}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cancellation ID
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {ticket.cancellationId || 'N/A'}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Created Date
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {formatDate(ticket.createdDate)}
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Assigned To
                      </label>
                      <div className="text-sm text-gray-900 bg-gray-50 p-2 rounded">
                        {ticket.assignedTo || 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Agent Query */}
                <div className="mt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Agent Query
                  </label>
                  <div className="text-sm text-gray-900 bg-gray-50 p-4 rounded border">
                    {ticket.agentQuery || 'N/A'}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comments Section */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-medium text-gray-900">Comments</h3>
              </div>

              <div className="p-6">
                {/* Add Comment Form */}
                <form onSubmit={handleAddComment} className="mb-6">
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Add Comment
                    </label>
                    <textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-green-500 focus:border-green-500"
                      rows="4"
                      placeholder="Enter your comment here..."
                      disabled={addingComment}
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={addingComment || !comment.trim()}
                    className="w-full bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {addingComment ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Adding Comment...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-comment mr-2"></i>
                        Add Comment
                      </>
                    )}
                  </button>
                </form>

                {/* Comments List */}
                <div className="space-y-4">
                  {ticket.comments && ticket.comments.length > 0 ? (
                    ticket.comments.map((comment, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-sm font-medium text-gray-900">
                            {comment.author || 'Support'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {formatDate(comment.createdDate)}
                          </div>
                        </div>
                        <div className="text-sm text-gray-700">
                          {comment.content || comment.text}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center text-gray-500 py-8">
                      <i className="fas fa-comments text-gray-300 text-3xl mb-2"></i>
                      <p>No comments yet</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportTicketDetails;